import React, { useEffect, useRef, useState } from 'react';
import axios from '../../axios';
import BaseModal, { ModalActions, SaveButton, CancelButton } from '../../containers/UI/Modal/BaseModal';
import classes from './Organization.module.css';

const OrganizationInviteModal = ({ isOpen, onClose, onInviteSuccess }) => {
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState(['administration']);
  const [showSelect, setShowSelect] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSelect(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInviteChange = (value) => {
    setInviteEmail(value);
    setInviteError(null);
    setInviteSuccess(null);
  };

  const handleRoleToggle = (value) => {
    setSelectedRoles((prev) => {
      if (prev.includes(value)) {
        if (prev.length === 1) return prev;
        return prev.filter((r) => r !== value);
      }
      return [...prev, value];
    });
    setInviteError(null);
    setInviteSuccess(null);
  };

  const handleClose = () => {
    if (inviteLoading) return;
    setInviteEmail('');
    setSelectedRoles(['administration']);
    setShowSelect(false);
    setInviteError(null);
    setInviteSuccess(null);
    if (onClose) {
      onClose();
    }
  };

  const handleInviteSubmit = async () => {
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      setInviteError('Please enter a valid email address');
      return;
    }

    try {
      setInviteLoading(true);
      setInviteError(null);
      setInviteSuccess(null);

      const response = await axios.post('/organization/invite', {
        email: inviteEmail.trim(),
        roles: selectedRoles
      });

      const message = response.data?.message || `Invitation sent to ${inviteEmail.trim()}`;
      setInviteSuccess(message);

      if (onInviteSuccess) {
        onInviteSuccess(message);
      }
    } catch (err) {
      setInviteError(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const roleOptions = [
    { value: 'administration', label: 'Administration' },
    { value: 'technical', label: 'Technical' }
  ];

  const selectedLabels = roleOptions
    .filter((r) => selectedRoles.includes(r.value))
    .map((r) => r.label)
    .join(', ');

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite member"
      size="default"
    >
      <div className={classes.inviteModalContent}>
        <p className={classes.inviteDescription}>
          Enter the email address of the person you want to invite to this organization.
        </p>

        {inviteError && (
          <div className={classes.error}>
            {inviteError}
          </div>
        )}

        {inviteSuccess && (
          <div className={classes.success}>
            {inviteSuccess}
          </div>
        )}

        <div className={classes.formRow}>
          <label className={classes.modalLabel}>Email</label>
          <input
            type="email"
            className={classes.input}
            value={inviteEmail}
            onChange={(e) => handleInviteChange(e.target.value)}
            placeholder="name@example.com"
            disabled={inviteLoading}
          />
        </div>
        <div className={classes.formRow}>
          <label className={classes.modalLabel}>Roles</label>
          <div className={classes.dropdownWrapper} ref={dropdownRef}>
            <button
              type="button"
              className={classes.dropdownTrigger}
              onClick={() => setShowSelect((p) => !p)}
              disabled={inviteLoading}
            >
              <span className={classes.dropdownTriggerText}>
                {selectedLabels || 'Select roles'}
              </span>
              <span className={classes.dropdownArrow}>{showSelect ? '▲' : '▼'}</span>
            </button>
            {showSelect && (
              <ul className={classes.dropdownMenu}>
                {roleOptions.map((r) => (
                  <li
                    key={r.value}
                    className={classes.dropdownItem}
                    onClick={() => handleRoleToggle(r.value)}
                  >
                    <span
                      className={`${classes.checkbox} ${
                        selectedRoles.includes(r.value) ? classes.checkboxChecked : ''
                      }`}
                    />
                    <span className={classes.dropdownItemLabel}>{r.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <ModalActions className={classes.inviteActions}>
          <CancelButton onClick={handleClose}>
            Cancel
          </CancelButton>
          <SaveButton 
            onClick={handleInviteSubmit}
            disabled={inviteLoading}
          >
            {inviteLoading ? 'Sending...' : 'Send invite'}
          </SaveButton>
        </ModalActions>
      </div>
    </BaseModal>
  );
};

export default OrganizationInviteModal;


